import { skipToken } from '@reduxjs/toolkit/query';
import { defineMessage, useIntl } from 'react-intl';
import { useLocation, useParams, useSearchParams } from 'react-router';

import type { BreadcrumbResolverResult } from '~/routing/types';

import { EntityType } from '~/enums/EntityType';
import { QueryParamType } from '~/enums/QueryParamType';
import { resolvePromptModelTargetEntityFieldId } from '~/features/fields/isLegacyPilotFieldId';
import { useEntityType } from '~/features/managed-entities/entity-types/hooks/useEntityTypes';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { atlas } from '~/redux/api/atlas';
import { api } from '~/redux/api/createApi';
import { k2 } from '~/redux/api/k2';
import { managedEntities } from '~/redux/api/managed-entities';
import { sylvanus } from '~/redux/api/sylvanus';
import { slices } from '~/redux/slices';
import { useAppSelector } from '~/rootState';

/**
 * Resolves the current workflow's name for a dynamic breadcrumb crumb on the
 * workflows-clm-evolution detail routes. Reference it directly from a route's
 * `handle.breadcrumbs` as `{ resolver: useWorkflowNameCrumb }`.
 */
export function useWorkflowNameCrumb(): BreadcrumbResolverResult {
  const { workflowId = '' } = useParams();

  // Match the args the workflow detail page issues
  // (`pages/workflows-clm-evolution/workflows/[workflowId]`) so this reads from
  // the shared RTK Query cache instead of firing a second request.
  const { data, isLoading } = k2.useGetWorkflowQuery(
    { workflowId, include: ['workflow_definition', 'acl'] },
    { skip: !workflowId },
  );

  return {
    label: data?.data?.attributes?.name ?? null,
    isLoading,
  };
}

/**
 * Resolves the current ticket's name for a dynamic breadcrumb crumb on the
 * workflows-clm-evolution request (ticket) detail routes. Reference it directly
 * from a route's `handle.breadcrumbs` as `{ resolver: useTicketNameCrumb }`.
 */
export function useTicketNameCrumb(): BreadcrumbResolverResult {
  const { ticketId = '' } = useParams();

  // Match the `{ ticketId }` args the ticket detail header/hook issue so this
  // reads from the shared RTK Query cache instead of firing a second request.
  const { data, isLoading } = k2.useGetTicketQuery(
    { ticketId },
    { skip: !ticketId },
  );

  return {
    label: data?.data?.attributes?.name ?? null,
    isLoading,
  };
}

/**
 * Names the ticket on the legacy workflow ticket routes.
 *
 * `useQueryState`, not `useQuery`: a crumb must only read what the page already
 * fetched. Subscribing from the bar makes the breadcrumb a second owner of the
 * query's lifecycle, which crashed the group detail page.
 */
export function useLegacyTicketNameCrumb(): BreadcrumbResolverResult {
  const { ticketId = '' } = useParams();
  const { data, isLoading } = api.endpoints.getTicketSummary.useQueryState({
    ticketId,
  });

  return { label: data?.name ?? null, isLoading };
}

/**
 * Resolves the current playbook's name for a dynamic breadcrumb crumb on the
 * playbook details route. Reference it directly from a route's
 * `handle.breadcrumbs` as `{ resolver: usePlaybookNameCrumb }`.
 */
export function usePlaybookNameCrumb(): BreadcrumbResolverResult {
  const { playbookId = '' } = useParams();

  // Match the args PlaybookDetailsPage issues so this reads from the shared
  // RTK Query cache instead of firing a second request.
  const { data, isLoading } = atlas.useGetPlaybookQuery(
    { playbookId },
    { skip: !playbookId },
  );

  return {
    label: data?.data?.attributes?.name ?? null,
    isLoading,
  };
}

/** Names the group on the workflow and documents group detail routes. */
export function useEntityGroupNameCrumb(): BreadcrumbResolverResult {
  const { groupId = '' } = useParams();
  const { data, isLoading } = managedEntities.endpoints.getGroup.useQueryState({
    groupId,
  });

  return { label: data?.data?.attributes?.name ?? null, isLoading };
}

/** Names the extraction table on the data extraction detail route. */
export function useDataExtractionNameCrumb(): BreadcrumbResolverResult {
  const { extractionId = '' } = useParams();
  // The page loads the table into this slice; there is no query to read.
  const extraction = useAppSelector((state) =>
    slices.dynamicDataExtraction.selectors.selectItemById(state, extractionId),
  );

  return { label: extraction?.title ?? null, isLoading: !extraction };
}

/**
 * Names the workflow on the legacy workflow builder route. The builder loads the
 * workflow through `useAsync`, not RTK Query, but hands it to redux — so the name
 * is a plain state read.
 */
export function useLegacyWorkflowNameCrumb(): BreadcrumbResolverResult {
  // `workflowLoad` assigns `name`, but the reducer's initial state omits it, so
  // it is absent from the inferred state type.
  const name = useAppSelector(
    (state) => (state.workflow as { name?: string }).name,
  );

  return { label: name || null, isLoading: !name };
}

const dashboardsCrumb = defineMessage({
  id: 'Routes.dashboardsTitle',
  defaultMessage: 'Dashboards',
  description: 'Title for the dashboards page route',
});

/**
 * Names the dashboard being viewed. `/dashboards` redirects here, so this crumb
 * stands alone rather than hanging off a list page. Legacy dashboards are keyed
 * by type rather than id and the page skips the query for them, so the section
 * name stands in — a crumb must never shimmer with nothing coming.
 */
export function useDashboardNameCrumb(): BreadcrumbResolverResult {
  const intl = useIntl();
  const { dashboardId = '' } = useParams();
  const { data, isLoading } =
    api.endpoints.getDashboardV2.useQueryState(dashboardId);

  if (data?.name) {
    return { label: data.name, isLoading: false };
  }
  return {
    label: isLoading ? null : intl.formatMessage(dashboardsCrumb),
    isLoading,
  };
}

const searchCrumb = defineMessage({
  id: 'Routes.searchTitle',
  defaultMessage: 'Search',
  description: 'Title for the search page route',
});

/**
 * Names the saved search being viewed. The sidenav lists it beside "Saved
 * Searches" rather than under Search, so this crumb stands alone. The deprecated
 * search page never fetches it, hence the fallback.
 */
export function useSavedSearchNameCrumb(): BreadcrumbResolverResult {
  const intl = useIntl();
  const { searchId = '' } = useParams();
  const { data, isLoading } =
    api.endpoints.getSavedSearch.useQueryState(searchId);

  if (data?.name) {
    return { label: data.name, isLoading: false };
  }
  return {
    label: isLoading ? null : intl.formatMessage(searchCrumb),
    isLoading,
  };
}

const clausesCrumb = defineMessage({
  id: 'PageHeader.subNavLinks.automation',
  defaultMessage: 'Clauses',
  description: 'Clause automation navigation link',
});

/**
 * Names the clause on the automation detail route. The page reads it from the
 * `clause` search param, so this does too — falling back to the section name for
 * the tab that carries no clause.
 */
export function useAutomationClauseCrumb(): BreadcrumbResolverResult {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const clause = searchParams.get(QueryParamType.Clause);

  return {
    label: clause || intl.formatMessage(clausesCrumb),
    isLoading: false,
  };
}

const modelCrumb = defineMessage({
  id: 'Routes.customAiModelCrumb',
  defaultMessage: 'Model',
  description: 'Breadcrumb text for a Custom AI model whose field is unknown',
});

/**
 * Names the Custom AI model by the field it targets, which is what the page
 * puts in its heading. Two cache reads, mirroring how the page derives it: the
 * model supplies the field id, the resolve endpoint the label. `/new` has no
 * model, so the fallback covers it.
 */
export function useFieldModelNameCrumb(): BreadcrumbResolverResult {
  const intl = useIntl();
  const { modelId = '' } = useParams();
  const isUnifiedFieldsM2Enabled = useFlag(FlagType.UnifiedFieldsM2);
  const { data: model, isLoading: isLoadingModel } =
    sylvanus.endpoints.getPromptModel.useQueryState({ modelId });
  const targetEntityFieldId = resolvePromptModelTargetEntityFieldId(
    model,
    isUnifiedFieldsM2Enabled,
  );
  const { data: targetEntity, isLoading: isResolvingEntity } =
    sylvanus.endpoints.resolvePromptModelTargetEntityDetails.useQueryState(
      targetEntityFieldId
        ? {
            entity: {
              id: String(targetEntityFieldId),
              type: EntityType.Field,
            },
          }
        : skipToken,
    );

  const isLoading = isLoadingModel || isResolvingEntity;
  if (targetEntity?.label) {
    return { label: targetEntity.label, isLoading: false };
  }
  return {
    label: isLoading ? null : intl.formatMessage(modelCrumb),
    isLoading,
  };
}

/** Names the entity type on the admin console detail route. */
export function useEntityTypeNameCrumb(): BreadcrumbResolverResult {
  const { entityTypeId = '' } = useParams();
  // Shares the list cache the page already populates — no extra request.
  const { entityType, isLoading } = useEntityType(entityTypeId);

  return {
    label: entityType?.name ?? null,
    isLoading,
  };
}

const creditsCrumb = defineMessage({
  id: 'Routes.creditsTitle',
  defaultMessage: 'Credits',
  description: 'Title for the credits page route',
});

/**
 * Names the administered client on the credits route. `:clientId` there is the
 * client being administered, and no endpoint returns a name for an arbitrary
 * client id — so the clients table hands it over in navigation state, and the
 * page name stands in when that is absent (refresh, direct link).
 */
export function useAdminClientNameCrumb(): BreadcrumbResolverResult {
  const intl = useIntl();
  const { state } = useLocation();
  const clientName = (state as { clientName?: string } | null)?.clientName;

  return {
    label: clientName || intl.formatMessage(creditsCrumb),
    isLoading: false,
  };
}

/**
 * Resolves the name of the document being viewed for the active-page breadcrumb
 * crumb on the request document viewer route. Reference it directly from a
 * route's `handle.breadcrumbs` as `{ resolver: useTicketDocumentNameCrumb }`.
 */
export function useTicketDocumentNameCrumb(): BreadcrumbResolverResult {
  const { ticketId = '', documentId = '' } = useParams();
  const { data, isLoading } = k2.useGetTicketQuery(
    { ticketId },
    { skip: !ticketId },
  );

  const documentName =
    data?.data?.attributes?.documents?.find(
      (document) => document.document_id === documentId,
    )?.name ?? null;

  return {
    label: documentName,
    isLoading,
  };
}
